
import React, { useState } from 'react';
import { TreatmentSession, SessionDocument } from '@/types/treatmentSession';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format, parseISO } from 'date-fns';
import { 
  FileText, 
  Upload, 
  FilePlus, 
  Eye, 
  Download, 
  Trash2,
  File,
  PieChart,
  FileImage
} from 'lucide-react';

interface SessionDocumentsManagerProps {
  session: TreatmentSession;
  onUploadDocument: (sessionId: number, document: Omit<SessionDocument, 'id' | 'sessionId' | 'uploadedAt'>) => Promise<SessionDocument>;
  onClose: () => void;
}

const SessionDocumentsManager: React.FC<SessionDocumentsManagerProps> = ({
  session,
  onUploadDocument,
  onClose
}) => {
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentType, setDocumentType] = useState<'consent' | 'lab_result' | 'xray' | 'report' | 'other'>('consent');
  const [isUploading, setIsUploading] = useState(false);
  
  const handleUpload = async () => {
    try {
      setIsUploading(true);
      
      // In a real app, this would upload a real file
      // For now, we'll just use a placeholder
      await onUploadDocument(session.id, {
        title: documentTitle,
        type: documentType,
        fileUrl: "/placeholder.svg",
        uploadedBy: 'doctor'
      });
      
      // Reset form
      setDocumentTitle('');
    } catch (err) {
      console.error('Error uploading document:', err);
    } finally {
      setIsUploading(false);
    }
  };
  
  const viewDocument = (document: SessionDocument) => {
    window.open(document.fileUrl, '_blank');
  };
  
  const downloadDocument = (document: SessionDocument) => {
    // In a real app, this would be a real download
    window.open(document.fileUrl, '_blank');
  };
  
  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'consent':
        return <FileText className="h-8 w-8 text-blue-500" />;
      case 'lab_result':
        return <PieChart className="h-8 w-8 text-purple-500" />;
      case 'xray':
        return <FileImage className="h-8 w-8 text-gray-500" />;
      case 'report':
        return <File className="h-8 w-8 text-green-500" />;
      default:
        return <File className="h-8 w-8 text-gray-500" />;
    }
  };
  
  const getDocumentTypeLabel = (type: string) => {
    switch (type) {
      case 'consent':
        return 'Consent Form';
      case 'lab_result':
        return 'Lab Result';
      case 'xray':
        return 'X-Ray';
      case 'report':
        return 'Report';
      default:
        return 'Other Document';
    }
  };
  
  return (
    <div className="py-4">
      <Tabs defaultValue="upload">
        <TabsList className="mb-4">
          <TabsTrigger value="upload">Upload New Document</TabsTrigger>
          <TabsTrigger value="manage">Manage Existing ({session.documents.length})</TabsTrigger>
        </TabsList>
        
        <TabsContent value="upload">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="document-title">Document Title</Label>
              <Input
                id="document-title"
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value)}
                placeholder="Enter document title..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="document-type">Document Type</Label>
              <Select 
                value={documentType} 
                onValueChange={(value) => setDocumentType(value as any)}
              >
                <SelectTrigger id="document-type">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consent">Consent Form</SelectItem>
                  <SelectItem value="lab_result">Lab Result</SelectItem>
                  <SelectItem value="xray">X-Ray</SelectItem>
                  <SelectItem value="report">Medical Report</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="border-2 border-dashed rounded-md p-6 text-center">
              <FilePlus className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                Drag and drop your file here, or click to browse
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Supported formats: PDF, JPG, PNG, DICOM
              </p>
              <Button variant="outline">
                <Upload className="h-4 w-4 mr-2" />
                Browse Files
              </Button>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpload} 
                disabled={!documentTitle || isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="manage">
          {session.documents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No documents uploaded yet
            </div>
          ) : (
            <div className="space-y-4">
              {session.documents.map((document) => (
                <Card key={document.id} className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex border-b">
                      <div className="w-16 h-16 flex items-center justify-center bg-muted">
                        {getDocumentIcon(document.type)}
                      </div>
                      <div className="flex-1 p-3">
                        <h3 className="font-medium">{document.title}</h3>
                        <p className="text-xs text-muted-foreground">
                          {getDocumentTypeLabel(document.type)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Uploaded on {format(parseISO(document.uploadedAt), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-end p-2 space-x-2 bg-muted/20">
                      <Button size="sm" variant="outline" onClick={() => viewDocument(document)}>
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadDocument(document)}>
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SessionDocumentsManager;
